import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import {
  DsButtonEmphasis,
  DsButtonSize,
  resolveDsButtonEmphasis,
} from './ds-button-v2.component';
import { DsIconComponent, DsIconSize } from '../icons/ds-icon.component';
import { DsIconName } from '../icons/ds-icons';

@Component({
  selector: 'ds-icon-button-v2',
  standalone: true,
  imports: [DsIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <button
      class="ds-icon-btn"
      [class]="hostClasses"
      [disabled]="disabled || null"
      [attr.aria-label]="ariaLabel"
      [attr.aria-haspopup]="ariaHasPopup || null"
      [attr.aria-expanded]="ariaExpanded"
      [attr.aria-controls]="ariaControls || null"
      (click)="clicked.emit($event)"
    >
      <ds-icon
        class="ds-icon-btn__icon"
        [name]="icon"
        [size]="iconSize"
      />
    </button>
  `,
  styles: [`
    :host { display: contents; }

    .ds-icon-btn {
      position:        relative;
      display:         inline-flex;
      align-items:     center;
      justify-content: center;
      border:          none;
      border-radius:   var(--ds-icon-button-border-radius);
      cursor:          pointer;
      padding:         0;
      flex-shrink:     0;
      overflow:        hidden;
      outline:         none;
    }

    .ds-icon-btn::before {
      content:       '';
      position:      absolute;
      inset:         0;
      border-radius: inherit;
      background:    var(--_hover-overlay, transparent);
      opacity:       0;
      pointer-events: none;
      transition:    opacity 120ms ease;
    }

    .ds-icon-btn > * {
      position: relative;
      z-index:  1;
    }

    .ds-icon-btn__icon {
      color: inherit;
    }

    .ds-icon-btn:hover:not(:disabled)::before {
      opacity: 1;
    }

    /* ── Sizes ─────────────────────────────────────────────── */
    .ds-icon-btn--m  { width: var(--ds-icon-button-m-container-size); height: var(--ds-icon-button-m-container-size); }
    .ds-icon-btn--s  { width: var(--ds-icon-button-s-container-size); height: var(--ds-icon-button-s-container-size); }
    .ds-icon-btn--xs { width: var(--ds-icon-button-xs-container-size); height: var(--ds-icon-button-xs-container-size); }

    /* ── Emphasis — normal ─────────────────────────────────── */
    .ds-icon-btn--primary {
      background:       var(--ds-icon-button-primary-background);
      color:            var(--ds-icon-button-primary-foreground);
      --_hover-overlay: var(--ds-icon-button-primary-hover-overlay);
    }

    .ds-icon-btn--primary-alt {
      background:       var(--ds-icon-button-primary-alt-background);
      color:            var(--ds-icon-button-primary-alt-foreground);
      --_hover-overlay: var(--ds-icon-button-primary-alt-hover-overlay);
    }

    .ds-icon-btn--secondary-outlined {
      background:       var(--ds-icon-button-secondary-outlined-background);
      color:            var(--ds-icon-button-secondary-outlined-foreground);
      --_hover-overlay: var(--ds-icon-button-secondary-outlined-hover-overlay);
      border:           var(--ds-icon-button-secondary-outlined-border-width) solid currentColor;
    }

    .ds-icon-btn--secondary-filled {
      background:       var(--ds-icon-button-secondary-filled-background);
      color:            var(--ds-icon-button-secondary-filled-foreground);
      --_hover-overlay: var(--ds-icon-button-secondary-filled-hover-overlay);
    }

    .ds-icon-btn--terciary {
      background:       var(--ds-icon-button-terciary-background);
      color:            var(--ds-icon-button-terciary-foreground);
      --_hover-overlay: var(--ds-icon-button-terciary-hover-overlay);
    }

    /* ── Emphasis — invert ─────────────────────────────────── */
    .ds-icon-btn--primary.ds-icon-btn--invert {
      background:       var(--ds-icon-button-primary-invert-background);
      color:            var(--ds-icon-button-primary-invert-foreground);
      --_hover-overlay: var(--ds-icon-button-primary-invert-hover-overlay);
    }

    .ds-icon-btn--secondary-outlined.ds-icon-btn--invert {
      background:       var(--ds-icon-button-secondary-outlined-invert-background);
      color:            var(--ds-icon-button-secondary-outlined-invert-foreground);
      --_hover-overlay: var(--ds-icon-button-secondary-outlined-invert-hover-overlay);
    }

    .ds-icon-btn--secondary-filled.ds-icon-btn--invert {
      background:       var(--ds-icon-button-secondary-filled-invert-background);
      color:            var(--ds-icon-button-secondary-filled-invert-foreground);
      --_hover-overlay: var(--ds-icon-button-secondary-filled-invert-hover-overlay);
    }

    .ds-icon-btn--terciary.ds-icon-btn--invert {
      background:       var(--ds-icon-button-terciary-invert-background);
      color:            var(--ds-icon-button-terciary-invert-foreground);
      --_hover-overlay: var(--ds-icon-button-terciary-invert-hover-overlay);
    }

    /* ── Disabled ───────────────────────────────────────────── */
    .ds-icon-btn:disabled {
      opacity: 0.4;
      cursor:  not-allowed;
    }

    /* ── Focus ring ─────────────────────────────────────────── */
    .ds-icon-btn:focus-visible {
      outline:        var(--ds-focus-stroke) solid var(--ds-focus-color);
      outline-offset: var(--ds-focus-gap);
    }
  `],
})
export class DsIconButtonV2Component {
  @Input() emphasis: DsButtonEmphasis = 'primary';
  @Input() size: DsButtonSize = 'm';
  @Input() disabled = false;
  @Input() invert   = false;
  @Input() ariaLabel = '';
  @Input() ariaHasPopup = '';
  @Input() ariaExpanded: boolean | null = null;
  @Input() ariaControls = '';
  @Input() icon: DsIconName | string = 'VisibilityFill';

  @Output() clicked = new EventEmitter<MouseEvent>();

  get hostClasses(): string {
    return [
      'ds-icon-btn',
      `ds-icon-btn--${this.size}`,
      `ds-icon-btn--${resolveDsButtonEmphasis(this.emphasis)}`,
      this.invert ? 'ds-icon-btn--invert' : '',
    ].filter(Boolean).join(' ');
  }

  get iconSize(): DsIconSize {
    return this.size === 'm'
      ? 'var(--ds-icon-button-m-icon-size)'
      : this.size === 's'
        ? 'var(--ds-icon-button-s-icon-size)'
        : 'var(--ds-icon-button-xs-icon-size)';
  }
}
